import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

export interface Config {
  gatewayUrl: string;
  taxGatewayUrl: string;
  identityBaseUrl: string;
  identityClientId: string;
  identityScope: string;
  identityRedirectUri: string;
  identityLogoutRedirectUri: string;
  authenticationEnabled: boolean;
  verifyTokens: boolean;
  entitlementsEnabled: boolean;
  verifyReferrer: boolean;
  identityEnabled: boolean;
  gatewayExternalPageKey: string;
  externalUrlPrefix: string;
  taxGatewayExternalPageKey: string;
  launchDarkly: {
    clientSideId: string;
    enabled?: boolean;
    user: {
      key: string;
      secondaryKey?: string;
      ip?: string;
      email?: string;
      name?: string;
      avatar?: string;
      firstName?: string;
      lastName?: string;
      country?: string;
      anonymous?: boolean;
      customAttributes?: Map<string, string>;
    };
  };
}

@Injectable()
export class ConfigService implements Config {
  private config: Config;
  public configInitialized: ReplaySubject<void>;

  constructor() {
    this.configInitialized = new ReplaySubject(1);
  }

  get gatewayUrl(): string {
    return this.getConfig().gatewayUrl;
  }

  get taxGatewayUrl(): string {
    return this.getConfig().taxGatewayUrl;
  }

  get identityBaseUrl(): string {
    return this.getConfig().identityBaseUrl;
  }

  get identityClientId(): string {
    return this.getConfig().identityClientId;
  }

  get identityScope(): string {
    return this.getConfig().identityScope;
  }

  get identityRedirectUri(): string {
    return this.getConfig().identityRedirectUri;
  }

  get identityLogoutRedirectUri(): string {
    return this.getConfig().identityLogoutRedirectUri;
  }

  get identityResponseType(): string {
    return 'token id_token';
  }

  get authenticationEnabled(): boolean {
    return this.getConfig().authenticationEnabled;
  }

  get verifyTokens(): boolean {
    return this.getConfig().verifyTokens;
  }

  get entitlementsEnabled(): boolean {
    return this.getConfig().entitlementsEnabled;
  }

  get verifyReferrer(): boolean {
    return this.getConfig().verifyReferrer;
  }

  /*
    If true, CUI will attempt to use Identity v2 for authentication.
    If false, CUI will use the ultipro-interim token scheme.
   */
  get identityEnabled(): boolean {
    return this.getConfig().identityEnabled;
  }

  get gatewayExternalPageKey(): string {
    return this.getConfig().gatewayExternalPageKey;
  }

  get externalUrlPrefix(): string {
    return this.getConfig().externalUrlPrefix;
  }

  get launchDarkly() {
    return this.getConfig().launchDarkly;
  }

  get taxGatewayExternalPageKey(): string {
    return this.getConfig().taxGatewayExternalPageKey;
  }

  init(config: Config) {
    if (!config) {
      throw new Error(`No configuration passed`);
    }

    if (this.config) {
      return;
    }

    const {
      gatewayUrl,
      taxGatewayUrl,
      identityBaseUrl,
      identityClientId,
      identityScope,
      identityRedirectUri,
      identityLogoutRedirectUri,
      verifyTokens,
      authenticationEnabled,
      entitlementsEnabled,
      verifyReferrer,
      identityEnabled,
      gatewayExternalPageKey,
      externalUrlPrefix,
      launchDarkly,
      taxGatewayExternalPageKey
    } = config;

    this.config = {
      gatewayUrl,
      taxGatewayUrl,
      identityBaseUrl,
      identityClientId,
      identityScope,
      identityRedirectUri,
      identityLogoutRedirectUri,
      authenticationEnabled,
      verifyTokens,
      entitlementsEnabled,
      verifyReferrer,
      identityEnabled,
      gatewayExternalPageKey,
      externalUrlPrefix,
      launchDarkly,
      taxGatewayExternalPageKey
    };
    this.configInitialized.next();
  }

  private getConfig(): Config {
    if (!this.config) {
      throw new Error(`Configuration is not initialized`);
    }

    return this.config;
  }
}
